import React, { useEffect, useState } from 'react'
import { Select } from '../ui/form/Select'
import { useAppDispatch, useAppSelector } from '../../redux/store'
import {
  getCcamCategoryLabels,
  getCcamCodesByCategory,
} from '../../redux/selectors'
import { createFilter } from 'react-select'
import { retrieveCcamCategoryLabels } from '../../core/use-cases/retrieveCcamCategoryLabels'
import { retrieveCcamCodes } from '../../core/use-cases/retrieveCcamCodes'
import * as S from '../styles'
import styled from '@emotion/styled'
import { mq } from '../breakpoints'
import { HelpButton } from '../ui/HelpButton'
import ccamExampleImage from '../../assets/ccam-example.png'
import loupeIcon from '../../assets/loupe.svg'
import { debounce } from '../../utils'
import { resetCcamCodes } from '../../redux/reducers/ccamCodes.reducer'
import { selectType } from '../../redux/reducers/simulation.reducer'
import LightBox from 'react-awesome-lightbox'
import 'react-awesome-lightbox/build/style.css'

export type CcamCodeFormProps = {
  disabled: boolean
  onSelectionChange: (code: string, categoryLabel: string) => void
}

export const CcamCodeForm = ({
  disabled,
  onSelectionChange,
}: CcamCodeFormProps) => {
  const dispatch = useAppDispatch()
  const simulationType = useAppSelector(selectType)
  const { value: ccamCategoryLabels, loading: ccamCategoryLabelsLoading } =
    useAppSelector(getCcamCategoryLabels)
  const [selectedCcamCategoryLabel, setSelectedCcamCategoryLabel] = useState('')
  const { value: ccamCodes, loading: ccamCodesLoading } = useAppSelector(
    getCcamCodesByCategory
  )
  const [selectedCcamCode, setSelectedCcamCode] = useState('')
  const [imageOpen, setImageOpen] = useState(false)

  useEffect(() => {
    dispatch(retrieveCcamCategoryLabels())
  }, [])

  useEffect(() => {
    setSelectedCcamCode('')
    setSelectedCcamCategoryLabel('')
  }, [simulationType])

  useEffect(() => {
    setSelectedCcamCode('')
    dispatch(resetCcamCodes)
  }, [selectedCcamCategoryLabel])

  return (
    <>
      {imageOpen && (
        <LightBox
          allowRotate={false}
          image={ccamExampleImage}
          onClose={() => setImageOpen(false)}
        />
      )}
      <Container disabled={disabled}>
        <S.FormContainer>
          <S.InputGroup>
            <S.InputLabel>
              Catégorie de soin <sup>1</sup>
            </S.InputLabel>
            <Select
              placeholder=''
              isDisabled={disabled}
              isLoading={ccamCategoryLabelsLoading}
              value={{
                label: selectedCcamCategoryLabel,
                value: selectedCcamCategoryLabel,
              }}
              options={ccamCategoryLabels.map(label => ({
                value: label,
                label,
              }))}
              onChange={option => {
                dispatch(resetCcamCodes())
                setSelectedCcamCategoryLabel(option.value)
                onSelectionChange('', option.value)
              }}
            />
          </S.InputGroup>
          <S.InputGroup>
            <S.InputLabel style={{ position: 'relative' }}>
              Code CCAM{' '}
              <HelpButton
                style={{ position: 'absolute', bottom: '0px', right: '-50px' }}
                content={
                  <div>
                    <h3>Où trouver le code CCAM&nbsp;?</h3>
                    <div
                      style={{ position: 'relative', cursor: 'pointer' }}
                      onClick={() => setImageOpen(true)}
                    >
                      <img
                        src={ccamExampleImage}
                        style={{ maxWidth: '300px' }}
                      />
                      <img
                        src={loupeIcon}
                        height={100}
                        width={100}
                        style={{
                          position: 'absolute',
                          left: 'calc(50% - 50px)',
                          top: 'calc(50% - 50px)',
                          opacity: '0.7',
                        }}
                      />
                    </div>
                  </div>
                }
              />
            </S.InputLabel>
            <Select
              placeholder='ex: BDQP003'
              noOptionsMessage={() =>
                selectedCcamCategoryLabel === ''
                  ? 'Sélectionnez une catégorie de soin'
                  : 'Commencez à saisir'
              }
              isDisabled={disabled}
              filterOption={createFilter({
                ignoreCase: true,
                matchFrom: 'start',
              })}
              isLoading={ccamCodesLoading}
              value={
                selectedCcamCode !== ''
                  ? {
                      label: selectedCcamCode,
                      value: selectedCcamCode,
                    }
                  : null
              }
              options={ccamCodes.sort().map(code => ({
                value: code,
                label: code,
              }))}
              onInputChange={search => {
                if (selectedCcamCategoryLabel === '' || search === '') {
                  return
                }

                debounce(() => {
                  dispatch(
                    retrieveCcamCodes({
                      categoryLabel: selectedCcamCategoryLabel,
                      search,
                    })
                  )
                }, 300)()
              }}
              onChange={option => {
                setSelectedCcamCode(option.value)
                onSelectionChange(option.value, selectedCcamCategoryLabel)
              }}
            />
          </S.InputGroup>
        </S.FormContainer>
      </Container>
    </>
  )
}

const Container = styled.div<{ disabled: boolean }>`
  flex: 1;
  display: ${props => (props.disabled ? 'none' : 'block')};

  ${mq.lg} {
    display: block;
    opacity: ${props => (props.disabled ? '0.5' : '1')};
  }
`
